/**
 * ListDragSensor — touch-based long-press-to-drag sensor.
 *
 * Uses raw TouchEvents (not PointerEvents) so it works correctly with
 * `touch-action: none` on the activator element. @dnd-kit's built-in
 * TouchSensor uses PointerEvents which get cancelled by the browser
 * when `touch-action: none` is set on a child element.
 *
 * Flow:
 * 1. touchstart on activator → record position, start delay timer
 * 2. touchmove during delay → if movement > tolerance, abort
 * 3. After delay with no movement → ACTIVATE drag
 * 4. Once active, forward touchmove coords to @dnd-kit
 * 5. touchend → end drag
 */

import type {
  Activators,
  SensorInstance,
  SensorProps,
  SensorOptions,
} from "@dnd-kit/core"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ListDragSensorOptions extends SensorOptions {
  activationConstraint: {
    /** Milliseconds to hold before drag activates */
    delay: number
    /** Max px movement allowed during delay */
    tolerance: number
  }
  onActivation?: (event: { event: Event }) => void
}

type Coordinates = { x: number; y: number }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getCoords(e: TouchEvent): Coordinates {
  const touch = e.touches[0]
  if (!touch) return { x: 0, y: 0 }
  return { x: touch.clientX, y: touch.clientY }
}

function getOwnerDocument(node: EventTarget | null): Document {
  if (node instanceof HTMLElement) return node.ownerDocument
  return document
}

function getWindow(node: EventTarget | null): Window {
  return getOwnerDocument(node).defaultView ?? window
}

// ---------------------------------------------------------------------------
// Sensor
// ---------------------------------------------------------------------------

export class ListDragSensor implements SensorInstance {
  public autoScrollEnabled = true

  private props: SensorProps<ListDragSensorOptions>
  private doc: Document
  private activated = false
  private initialCoords: Coordinates
  private timeoutId: ReturnType<typeof setTimeout> | null = null

  // Bound handlers
  private boundMove: (e: TouchEvent) => void
  private boundEnd: (e: TouchEvent) => void
  private boundCancel: (e: TouchEvent) => void

  // ── Activators ─────────────────────────────────────────────────────────

  static activators: Activators<ListDragSensorOptions> = [
    {
      eventName: "onTouchStart" as const,
      handler: (
        { nativeEvent: event }: React.TouchEvent,
        { onActivation }: ListDragSensorOptions,
      ) => {
        if (event.touches.length > 1) return false
        onActivation?.({ event })
        return true
      },
    },
  ]

  // ── Global setup (non-passive touchmove for preventDefault) ────────────

  static setup() {
    const handler = () => {
      /* existence of non-passive listener enables preventDefault */
    }
    window.addEventListener("touchmove", handler, {
      capture: false,
      passive: false,
    })
    return function teardown() {
      window.removeEventListener("touchmove", handler)
    }
  }

  // ── Constructor ────────────────────────────────────────────────────────

  constructor(props: SensorProps<ListDragSensorOptions>) {
    this.props = props
    this.doc = getOwnerDocument(props.event.target)
    this.initialCoords = getCoords(props.event as unknown as TouchEvent)

    this.boundMove = this.handleMove.bind(this)
    this.boundEnd = this.handleEnd.bind(this)
    this.boundCancel = this.handleCancel.bind(this)

    this.attach()
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────

  private attach() {
    const { delay, tolerance } = this.props.options.activationConstraint
    const win = getWindow(this.props.event.target)

    win.addEventListener("touchmove", this.boundMove, { passive: false })
    win.addEventListener("touchend", this.boundEnd)
    win.addEventListener("touchcancel", this.boundCancel)
    this.doc.addEventListener("contextmenu", preventDefault, { capture: true })

    this.props.onPending(
      this.props.active,
      { delay, tolerance },
      this.initialCoords,
    )

    this.timeoutId = setTimeout(() => {
      this.timeoutId = null
      this.activate()
    }, delay)
  }

  private detach() {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }

    const win = getWindow(this.props.event.target)
    win.removeEventListener("touchmove", this.boundMove)
    win.removeEventListener("touchend", this.boundEnd)
    win.removeEventListener("touchcancel", this.boundCancel)

    setTimeout(() => {
      this.doc.removeEventListener("contextmenu", preventDefault, {
        capture: true,
      })
    }, 400)
  }

  // ── Activation ─────────────────────────────────────────────────────────

  private activate() {
    if (this.activated) return
    this.activated = true

    this.doc.getSelection()?.removeAllRanges()
    this.doc.addEventListener("click", stopPropagation, { capture: true })

    this.props.onStart(this.initialCoords)
  }

  // ── Event Handlers ─────────────────────────────────────────────────────

  private handleMove(e: TouchEvent) {
    const coords = getCoords(e)

    // ── Drag active: forward to @dnd-kit ──────────────────────────────
    if (this.activated) {
      if (e.cancelable) e.preventDefault()
      this.props.onMove(coords)
      return
    }

    // ── Delay phase: check tolerance ─────────────────────────────────
    const deltaX = Math.abs(coords.x - this.initialCoords.x)
    const deltaY = Math.abs(coords.y - this.initialCoords.y)
    const { tolerance } = this.props.options.activationConstraint

    if (deltaX > tolerance || deltaY > tolerance) {
      // Movement exceeded tolerance — abort.
      // Must call onCancel() to reset @dnd-kit's internal activeRef gate.
      // onAbort() alone does NOT clear activeRef.current, which permanently
      // blocks all future sensor activations (the root cause of DnD breaking
      // after swipe-to-select on mobile).
      this.detach()
      this.props.onAbort(this.props.active)
      this.props.onCancel()
      return
    }

    // Within tolerance — prevent default to hold the touch
    if (e.cancelable) e.preventDefault()
  }

  private handleEnd(_e: TouchEvent) {
    this.detach()
    if (this.activated) {
      this.props.onEnd()
      this.cleanup()
    } else {
      // Follow PointerSensor pattern: call onAbort for notification,
      // then onEnd to reset @dnd-kit's activeRef.current gate.
      this.props.onAbort(this.props.active)
      this.props.onEnd()
    }
  }

  private handleCancel(_e: TouchEvent) {
    this.detach()
    if (this.activated) {
      this.props.onCancel()
      // Must cleanup capture click listener added during activate().
      // Without this, the listener persists forever and swallows all click
      // events on the document — breaking section toggles and other clicks.
      this.cleanup()
    } else {
      // Follow PointerSensor pattern: call onAbort for notification,
      // then onCancel to reset @dnd-kit's activeRef.current gate.
      this.props.onAbort(this.props.active)
      this.props.onCancel()
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  private cleanup() {
    // Use a 400ms delay — on iOS Safari the synthetic click event after a
    // long-press can arrive well after touchend (up to 300ms). The previous
    // 50ms timeout was too short, letting the click through to handlers like
    // handleSectionClick which would toggle/clear the selection.
    setTimeout(() => {
      this.doc.removeEventListener("click", stopPropagation, { capture: true })
    }, 400)
  }
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function preventDefault(e: Event) {
  e.preventDefault()
}

function stopPropagation(e: Event) {
  e.stopPropagation()
}
